import { __assign, __read } from "tslib";
import { useEffect, useRef, useState } from 'react';
import useMountedState from 'react-use/lib/useMountedState';
/**
 * Based on https://github.com/streamich/react-use/blob/master/src/useSlider.ts
 * Returns position x/y coordinates which can be directly used in transform: translate().
 * @param scale Can be used when we want to scale the movement if we are moving a scaled element. We need to do it
 *   here because we don't want to change the pos when scale changes.
 * @param bounds If set the panning cannot go outside of those bounds.
 */
export function usePanning(_a) {
    var _b = _a === void 0 ? { scale: 1 } : _a, _c = _b.scale, scale = _c === void 0 ? 1 : _c, bounds = _b.bounds;
    var isMounted = useMountedState();
    var isPanning = useRef(false);
    var frame = useRef(0);
    var panRef = useRef(null);
    // We need to keep some state so we can compute the position diff and add that to the previous position.
    var startMousePosition = useRef({ x: 0, y: 0 });
    var prevPosition = useRef({ x: 0, y: 0 });
    // We cannot use the state as that would rerun the effect on each state change which we don't want so we have to keep
    // separate variable for the state that won't cause useEffect eval
    var currentPosition = useRef({ x: 0, y: 0 });
    var _d = __read(useState({
        isPanning: false,
        position: { x: 0, y: 0 },
    }), 2), state = _d[0], setState = _d[1];
    useEffect(function () {
        var startPanning = function (event) {
            if (!isPanning.current && isMounted()) {
                isPanning.current = true;
                // Snapshot the current position of both mouse pointer and the element
                startMousePosition.current = getEventXY(event);
                prevPosition.current = __assign({}, currentPosition.current);
                setState(function (state) { return (__assign(__assign({}, state), { isPanning: true })); });
                bindEvents();
            }
        };
        var stopPanning = function () {
            if (isPanning.current && isMounted()) {
                isPanning.current = false;
                setState(function (state) { return (__assign(__assign({}, state), { isPanning: false })); });
                unbindEvents();
            }
        };
        var onPanStart = function (event) {
            startPanning(event);
            onPan(event);
        };
        var bindEvents = function () {
            document.addEventListener('mousemove', onPan);
            document.addEventListener('mouseup', stopPanning);
            document.addEventListener('touchmove', onPan);
            document.addEventListener('touchend', stopPanning);
        };
        var unbindEvents = function () {
            document.removeEventListener('mousemove', onPan);
            document.removeEventListener('mouseup', stopPanning);
            document.removeEventListener('touchmove', onPan);
            document.removeEventListener('touchend', stopPanning);
        };
        var onPan = function (event) {
            cancelAnimationFrame(frame.current);
            var pos = getEventXY(event);
            frame.current = requestAnimationFrame(function () {
                if (isMounted() && panRef.current) {
                    // Get the diff by which we moved the mouse.
                    var xDiff = pos.x - startMousePosition.current.x;
                    var yDiff = pos.y - startMousePosition.current.y;
                    // Add the diff to the position from the moment we started panning.
                    currentPosition.current = {
                        x: inBounds(prevPosition.current.x + xDiff / scale, bounds === null || bounds === void 0 ? void 0 : bounds.left, bounds === null || bounds === void 0 ? void 0 : bounds.right),
                        y: inBounds(prevPosition.current.y + yDiff / scale, bounds === null || bounds === void 0 ? void 0 : bounds.top, bounds === null || bounds === void 0 ? void 0 : bounds.bottom),
                    };
                    setState(function (state) { return (__assign(__assign({}, state), { position: __assign({}, currentPosition.current) })); });
                }
            });
        };
        var ref = panRef.current;
        if (ref) {
            ref.addEventListener('mousedown', onPanStart);
            ref.addEventListener('touchstart', onPanStart);
        }
        return function () {
            if (ref) {
                ref.removeEventListener('mousedown', onPanStart);
                ref.removeEventListener('touchstart', onPanStart);
            }
        };
    }, [scale, bounds === null || bounds === void 0 ? void 0 : bounds.left, bounds === null || bounds === void 0 ? void 0 : bounds.right, bounds === null || bounds === void 0 ? void 0 : bounds.top, bounds === null || bounds === void 0 ? void 0 : bounds.bottom, isMounted]);
    return { state: state, ref: panRef };
}
function inBounds(value, min, max) {
    return Math.min(Math.max(value, min !== null && min !== void 0 ? min : -Infinity), max !== null && max !== void 0 ? max : Infinity);
}
function getEventXY(event) {
    if (event.changedTouches) {
        var e = event;
        return { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
    }
    else {
        var e = event;
        return { x: e.clientX, y: e.clientY };
    }
}
//# sourceMappingURL=usePanning.js.map